// Integration types - Knowledge base integrations sync data to the knowledge base
export const KNOWLEDGE_BASE_INTEGRATIONS = [
  "atlassian",
  "notion",
  "google_docs",
  "website",
];

// Communication integrations - Chat/messaging platforms
export const COMMUNICATION_INTEGRATIONS = ["prosp", "heyreach", "salesforge"];

// Email/messaging service integrations
export const EMAIL_INTEGRATIONS = ["aws", "sendgrid", "imap", "office365", "gmail"];

// Email integrations appropriate for bulk/marketing sends (ESPs, not personal inboxes)
export const MARKETING_EMAIL_INTEGRATION_TYPES = ["aws", "sendgrid"];

// Automation/workflow integrations
export const AUTOMATION_INTEGRATIONS = ["zapier"];

// Publishing integrations
export const PUBLISHING_INTEGRATIONS = ["wordpress", "webflow"];

// Social media automation integrations (Composio-based)
export const SOCIAL_INTEGRATIONS = ["linkedin", "linkedin_login", "facebook", "instagram"];

// Browser session integrations
export const BROWSER_SESSION_INTEGRATIONS = ["browser_session"];

// Extension integrations
export const EXTENSION_INTEGRATIONS = ["chrome"];

// Video generation integrations
export const VIDEO_INTEGRATIONS = ["heygen"];

// SMS integrations
export const SMS_INTEGRATIONS = ["twilio"];

// Tool integrations - MCP servers
export const TOOL_INTEGRATIONS = ["mcp"];

// Composio-powered integrations are identified by provider="composio"
// Use the provider query parameter when fetching: ?provider=composio
// Import INTEGRATION_PROVIDER_COMPOSIO from "../../constants" for the constant value

// E-commerce integrations
export const ECOMMERCE_INTEGRATIONS = ["shopify"];

// Scheduling/calendar integrations
export const SCHEDULING_INTEGRATIONS = ["scheduling"];

// CRM integrations
export const CRM_INTEGRATIONS = ["hubspot", "gohighlevel", "salesforce"];

// Data enrichment integrations
export const DATA_ENRICHMENT_INTEGRATIONS = ["apollo", "findymail", "bettercontact"];

// Direct mail integrations
export const DIRECT_MAIL_INTEGRATIONS = ["thanksio"];

// Advertising platform integrations
export const ADVERTISING_INTEGRATIONS = ["meta_ads"];

// Analytics integrations
export const ANALYTICS_INTEGRATIONS = ["google_analytics"];

// Mapping integration type codes to categories
export const INTEGRATION_TYPES: Record<string, string> = {
  slack: "communication",
  teams: "communication",
  discord: "communication",
  atlassian: "knowledge_base",
  notion: "knowledge_base",
  google_docs: "knowledge_base",
  website: "knowledge_base",
  wordpress: "publishing",
  webflow: "publishing",
  linkedin: "social",
  linkedin_login: "social",
  aws: "email",
  sendgrid: "email",
  imap: "email",
  office365: "email",
  gmail: "email",
  n8n: "automation",
  zapier: "automation",
  chrome: "extension",
  heygen: "video",
  twilio: "sms",
  mcp: "tools",
  browser_session: "browser_session",
  shopify: "ecommerce",
  scheduling: "scheduling",
  hubspot: "crm",
  gohighlevel: "crm",
  salesforce: "crm",
  facebook: "social",
  instagram: "social",
  apollo: "data_enrichment",
  findymail: "data_enrichment",
  bettercontact: "data_enrichment",
  prosp: "communication",
  heyreach: "communication",
  salesforge: "communication",
  thanksio: "direct_mail",
  meta_ads: "advertising",
  google_analytics: "analytics",
};

// Mapping integration type codes to display names
export const INTEGRATION_NAMES: Record<string, string> = {
  slack: "Slack",
  teams: "Microsoft Teams",
  discord: "Discord",
  atlassian: "Confluence",
  notion: "Notion",
  google_docs: "Google Drive",
  website: "Website Crawler",
  wordpress: "WordPress",
  webflow: "Webflow",
  linkedin: "LinkedIn",
  linkedin_login: "LinkedIn Login",
  aws: "AWS SES",
  sendgrid: "SendGrid",
  imap: "IMAP Email",
  office365: "Office 365 Email",
  gmail: "Gmail",
  n8n: "n8n",
  zapier: "Zapier",
  chrome: "Chrome Extension",
  heygen: "HeyGen",
  twilio: "Twilio",
  mcp: "MCP Tool",
  browser_session: "Browser Session",
  shopify: "Shopify",
  scheduling: "Calendar Scheduling",
  hubspot: "HubSpot",
  gohighlevel: "GoHighLevel",
  salesforce: "Salesforce",
  facebook: "Facebook",
  instagram: "Instagram",
  apollo: "Apollo",
  findymail: "Findymail",
  bettercontact: "Better Contact",
  prosp: "Prosp",
  heyreach: "HeyReach",
  salesforge: "Salesforge",
  thanksio: "Thanks.io",
  meta_ads: "Meta Ads",
  google_analytics: "Google Analytics",
};

// Integrations that require instance name field
export const INTEGRATIONS_WITH_INSTANCE_NAME = [
  "google_docs",
  "atlassian",
  "notion",
  "aws",
  "sendgrid",
  "imap",
  "office365",
  "gmail",
  "website",
  "heygen",
  "linkedin",
  "linkedin_login",
  "twilio",
  "mcp",
  "browser_session",
  "shopify",
  "scheduling",
  "hubspot",
  "gohighlevel",
  "salesforce",
  "facebook",
  "instagram",
  "apollo",
  "findymail",
  "bettercontact",
  "prosp",
  "heyreach",
  "salesforge",
  "webflow",
  "thanksio",
  "meta_ads",
  "google_analytics",
];

// Integrations that require destination path field (knowledge base integrations)
export const INTEGRATIONS_WITH_DESTINATION_PATH = [
  "google_docs",
  "atlassian",
  "notion",
  "website",
];

// Integrations that have custom forms in the dialog
export const INTEGRATIONS_WITH_FORMS = [
  "google_docs",
  "wordpress",
  "aws",
  "sendgrid",
  "imap",
  "office365",
  "gmail",
  "atlassian",
  "notion",
  "website",
  "heygen",
  "linkedin",
  "linkedin_login",
  "twilio",
  "mcp",
  "browser_session",
  "shopify",
  "scheduling",
  "hubspot",
  "gohighlevel",
  "salesforce",
  "facebook",
  "instagram",
  "apollo",
  "findymail",
  "bettercontact",
  "prosp",
  "heyreach",
  "salesforge",
  "webflow",
  "thanksio",
  "meta_ads",
  "google_analytics",
];

// Integrations that have editable settings (same as forms for now)
export const EDITABLE_INTEGRATIONS = INTEGRATIONS_WITH_FORMS;

// Integrations that require OAuth apps configured for specific domains (hidden on whitelabel domains)
export const OAUTH_INTEGRATIONS: string[] = [];

// Mapping integration type codes to logo asset paths
export const INTEGRATION_LOGOS: Record<string, string> = {
  slack: require("../../img/slack-circle.png"),
  teams: require("../../img/teams.svg"),
  atlassian: require("../../img/confluence.svg"),
  notion: require("../../img/notion.svg"),
  discord: require("../../img/discord.svg"),
  google_docs: require("../../img/gdrive.png"),
  wordpress: require("../../img/wordpress_logo.png"),
  linkedin: require("../../img/linkedin.png"), // TODO: Add LinkedIn logo image
  linkedin_login: require("../../img/linkedin.png"), // Use same LinkedIn logo
  aws: require("../../img/aws_circle.png"),
  sendgrid: require("../../img/sendgrid_square.png"),
  imap: require("../../img/mail-blue.png"),
  office365: require("../../img/office-365-logo.png"),
  gmail: require("../../img/google-logo.png"),
  website: require("../../img/website.png"),
  n8n: require("../../img/n8n-square.png"),
  heygen: require("../../img/heygen-square.png"),
  twilio: require("../../img/twilio.png"),
  mcp: require("../../img/mcp.png"),
  browser_session: require("../../img/chrome.svg"),
  shopify: require("../../img/shopify.png"),
  scheduling: require("../../img/calendar.png"),
  hubspot: require("../../img/hubspot.svg"),
  gohighlevel: require("../../img/gohighlevel.svg"),
  salesforce: require("../../img/salesforce.png"),
  facebook: require("../../img/facebook.png"),
  instagram: require("../../img/instagram.png"),
  apollo: require("../../img/apollo.svg"),
  findymail: require("../../img/findymail.png"),
  bettercontact: require("../../img/bettercontact.png"),
  audiencelab: require("../../img/audiencelab.png"),
  prosp: require("../../img/prosp.png"),
  heyreach: require("../../img/heyreach.png"),
  salesforge: require("../../img/salesforge.svg"),
  thanksio: require("../../img/thanksio.png"),
  meta_ads: require("../../img/meta.png"),
  google_analytics: require("../../img/google-logo.png"),
};
